import { LoaderCircle, LogIn } from 'lucide-react';
import { type FormEvent, lazy, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { runtimeConfig } from './config';
import { RequireAnyPermission, RequirePermission } from '../auth/RequirePermission';
import { apiReferencePermissions } from '../modules/apireference/catalog';
import { useAuth } from '../auth/OidcProvider';
import { AppShell } from '../shell/AppShell';
import { PageContent } from './PageContent';
import { SessionExpiredDialog } from '../auth/SessionExpiredDialog';

const DamModule = lazy(() => import('../modules/dam/DamModule').then((module) => ({ default: module.DamModule })));
const DocumentsModule = lazy(() => import('../modules/documents/DocumentsModule').then((module) => ({ default: module.DocumentsModule })));
const SftpgoModule = lazy(() => import('../modules/sftpgo/SftpgoModule').then((module) => ({ default: module.SftpgoModule })));
const MasterdataModule = lazy(() => import('../modules/masterdata/MasterdataModule').then((module) => ({ default: module.MasterdataModule })));
const ApiReferenceModule = lazy(() => import('../modules/apireference/ApiReferenceModule').then((module) => ({ default: module.ApiReferenceModule })));
const MailModule = lazy(() => import('../modules/mail/MailModule').then((module) => ({ default: module.MailModule })));
const Dashboard = lazy(() => import('../shell/Dashboard').then((module) => ({ default: module.Dashboard })));

function LoginPage() {
  const auth = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const submitDevelopmentLogin = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await auth.login({ username, password });
    setPassword('');
  };

  return <main className="login-page">
    <section className="login-card">
      <div className="login-intro">
        <img className="login-brand" src="/brand/ges-logo-white.png" alt="GES eG" />
        <h1>{runtimeConfig.demoMode ? <>Lokale Vorschau.<br />Direkter Zugang.</> : <>Alle Fachsysteme.<br />Ein sicherer Zugang.</>}</h1>
        <p>{runtimeConfig.demoMode
          ? 'Dieser Entwicklungsmodus benötigt weder Authentik noch laufende Fachserver.'
          : 'Ein zentraler Zugang zu den freigeschalteten GES-Fachsystemen.'}</p>
      </div>
      <div className="login-auth">
        {runtimeConfig.demoMode
          ? <>
            <h2>Lokal anmelden</h2>
            <p className="dev-login-note">Beliebige nicht-leere Zugangsdaten öffnen alle Demo-Module. Das Passwort wird weder gespeichert noch übertragen.</p>
            <form className="login-form" onSubmit={(event) => void submitDevelopmentLogin(event)}>
              <label className="field">
                <span>Benutzername</span>
                <input value={username} onChange={(event) => setUsername(event.target.value)} autoComplete="username" maxLength={100} required autoFocus />
              </label>
              <label className="field">
                <span>Passwort</span>
                <input type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="current-password" required />
              </label>
              {auth.error && <p className="error-copy" role="alert">{auth.error}</p>}
              <button className="button primary" type="submit"><LogIn size={18} /> Lokal anmelden</button>
            </form>
          </>
          : <>
            <h2>Mit GES-Konto anmelden</h2>
            <p>Melde dich über Authentik an. Danach siehst du ausschließlich die Module und Funktionen, für die du berechtigt bist.</p>
            {auth.error && <p className="error-copy" role="alert">{auth.error}</p>}
            <button className="button primary" onClick={() => void auth.login()}><LogIn size={18} /> Mit Authentik anmelden</button>
          </>}
      </div>
    </section>
  </main>;
}

export function App() {
  const auth = useAuth();
  if (auth.loading) return <main className="center-state"><LoaderCircle className="spin" /><p>Sitzung wird geprüft …</p></main>;
  if (!auth.session) return <LoginPage />;
  // The module tree stays mounted behind the dialog - that is the whole point of rendering the
  // expiry as an overlay rather than dropping the session and falling back to the login page.
  return <>
    {auth.expired && <SessionExpiredDialog />}
    <Routes>
      <Route element={<AppShell />}>
        <Route element={<PageContent />}>
          <Route index element={<Dashboard />} />
          <Route path="dam/*" element={<RequirePermission permission="dam.read"><DamModule /></RequirePermission>} />
          <Route path="documents/*" element={<RequirePermission permission="docs.read"><DocumentsModule /></RequirePermission>} />
          <Route path="sftpgo/*" element={<RequirePermission permission="sftp.read"><SftpgoModule /></RequirePermission>} />
          <Route path="masterdata/*" element={<RequirePermission permission="std.read"><MasterdataModule /></RequirePermission>} />
          <Route path="mail/*" element={<RequirePermission permission="mail.read"><MailModule /></RequirePermission>} />
          <Route path="api-reference/*" element={<RequireAnyPermission permissions={apiReferencePermissions}><ApiReferenceModule /></RequireAnyPermission>} />
          <Route path="auth/callback" element={<Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Route>
    </Routes>
  </>;
}
